/**
 * Keyboard plumbing for the modal coachmark steps.
 *
 * ── WHY A WINDOW CAPTURE LISTENER ───────────────────────────────────────────
 *
 * A floating step can sit over an open antd Drawer or Modal. rc-portal wires
 * Esc on `window` in the BUBBLE phase, so a bubble listener of ours would let
 * the Drawer close in the same keystroke. Listening on `window` in the
 * CAPTURE phase runs before anything else in the page, and
 * `stopImmediatePropagation()` there keeps the event from ever reaching the
 * Drawer, the quick-search shortcut, or the target underneath.
 */
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');
/** Tabbable elements inside `root`, in DOM order, sentinels excluded. */
export function focusableWithin(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.hasAttribute('data-bk-focus-sentinel') && !el.closest('[hidden]'));
}
/** Typing targets, where arrow keys belong to the caret. */
export function isEditable(el) {
    if (!(el instanceof HTMLElement))
        return false;
    if (el.isContentEditable)
        return true;
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT')
        return true;
    if (tag !== 'INPUT')
        return false;
    const type = el.type;
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
}
/** Swallow a key completely: no default, no other listener. */
export function swallow(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
}
/**
 * Tab / Shift+Tab cycle inside `panel`. Focus that has escaped the panel
 * (it cannot, short of a script) is brought back to the first control.
 * Returns true when it moved focus itself.
 */
export function trapTab(event, panel) {
    const items = focusableWithin(panel);
    const active = document.activeElement;
    if (items.length === 0) {
        event.preventDefault();
        return true;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (!active || !panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return true;
    }
    const index = items.indexOf(active);
    if (event.shiftKey && (index <= 0)) {
        event.preventDefault();
        last.focus();
        return true;
    }
    if (!event.shiftKey && index === items.length - 1) {
        event.preventDefault();
        first.focus();
        return true;
    }
    return false;
}
/**
 * RTL: ArrowLeft goes forward (the reading direction), ArrowRight back;
 * mirrored in LTR. `null` for any other key.
 */
export function arrowIntent(key, dir) {
    if (key === 'ArrowLeft')
        return dir === 'rtl' ? 'next' : 'prev';
    if (key === 'ArrowRight')
        return dir === 'rtl' ? 'prev' : 'next';
    return null;
}
/** The quick-search shortcuts a floating tour keeps from opening underneath it. */
export function isQuickSearchShortcut(event) {
    if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey)
        return true;
    return (event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K');
}
//# sourceMappingURL=focus-trap.js.map